import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function _createForOfIteratorHelper(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (!t) { if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var _n = 0, F = function F() {}; return { s: F, n: function n() { return _n >= r.length ? { done: !0 } : { done: !1, value: r[_n++] }; }, e: function e(r) { throw r; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var o, a = !0, u = !1; return { s: function s() { t = t.call(r); }, n: function n() { var r = t.next(); return a = r.done, r; }, e: function e(r) { u = !0, o = r; }, f: function f() { try { a || null == t["return"] || t["return"](); } finally { if (u) throw o; } } }; }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { LTBreadcrumbs, LTButton, LTTooltip } from '@lambdatestincprivate/lt-components';
import { closePack, downloadPack, openIssueDrawer, usePackState } from '../data/pack-store';
import { goHome, openPackFromInput } from '../app/open-pack';
import { useViewerMode } from '../app/mode';
import { DownloadIcon, LinkExternalIcon } from '@primer/octicons-react';
import { hyperExecuteHref } from './links';
import { truncateMiddle } from './format';
import { applyDeepLink, closeDetailInPlace, closeDetailWithUrl, listenForTestParam, openDetailWithUrl, syncViewParams, writeTabParam } from '../app/url-sync';
import { TestDetailMount } from './Detail/TestDetailPage';
import { HyperExecuteLink } from './Detail/Header/HyperExecuteLink';
import { TestListPopover } from './Detail/Header/TestListPopover';
import { CopyLinkButton } from './CopyLink/CopyLinkButton';
import { EV_DOWNLOAD_PACK_EVENT, EV_OPEN_PALETTE_EVENT } from '../app/host-events';
import { CommandPalette, PALETTE_SHORTCUT_LABEL } from './Palette/CommandPalette';
import { SearchGlyph } from './icons/SearchGlyph';
import { InstanceList } from './Instances/InstanceList';
import { Landing } from './Landing/Landing';
import { RunHeader } from './RunHeader/RunHeader';
import { NeedAttention } from './Report/NeedAttention';
import { SummaryStrip } from './Summary/SummaryStrip';
import { RunTabs } from './Tabs/RunTabs';
import { ErrorScreen } from './states/ErrorScreen';
import { GhConnectGate } from './states/GhConnectGate';
import { GhChooseScreen } from './states/GhChooseScreen';
import { chooseGhEntry } from '../data/pack-store';
import { beginGhLogin } from '../app/gh-auth';
import { OpeningPack } from './states/OpeningPack';
import { CoverageTab } from './Coverage/CoverageTab';
import { IssuesTab } from './Issues/IssuesTab';
import { RcaDrawer } from './Issues/RcaDrawer';
import { AppHeader } from './AppHeader';
import { HeaderLinks } from './HeaderLinks';
import { TestMuBrandLogo } from './icons/TestMuBrandLogo';
import styles from './app-shell.module.css';

// Lazy: the internal explorer must be BUNDLE-inert for prod users, not
// just behavior-inert — the chunk only loads when the flag-gated tab
// actually renders.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var ExplorerTab = /*#__PURE__*/React.lazy(function () {
  return import('./Explorer/ExplorerTab').then(function (m) {
    return {
      "default": m.ExplorerTab
    };
  });
});

/**
 * Slim top bar — STANDALONE viewer only (the TMS mount has the host's own
 * header; its search trigger lives there, see EV_OPEN_PALETTE_EVENT).
 * Left: "Evidence" plus the run title as a breadcrumb path when the pack
 * authors one; right: palette search, then download the .evidence file.
 */
/** One breadcrumb level after "Evidence"; clickable ones navigate up. */

var TAB_LABELS = {
  report: 'Report',
  instances: 'Test Instances',
  issues: 'Issues',
  coverage: 'Coverage',
  explorer: 'Explorer'
};

/** Long names middle-truncate; the full text stays in the title. */
var CRUMB_MAX_CHARS = 60;
function TopBar(_ref) {
  var crumbs = _ref.crumbs,
    jobId = _ref.jobId,
    currentTestId = _ref.currentTestId,
    onOpenSearch = _ref.onOpenSearch;
  var hyperHref = hyperExecuteHref(jobId);
  return /*#__PURE__*/_jsxs("div", {
    className: styles.topBar,
    children: [/*#__PURE__*/_jsxs("span", {
      className: styles.topBarLeft,
      children: [/*#__PURE__*/_jsx("button", {
        type: "button",
        className: styles.topBarBrand,
        onClick: goHome,
        "aria-label": "Evidence Viewer \u2014 back to home",
        children: /*#__PURE__*/_jsx(TestMuBrandLogo, {
          height: 28
        })
      }), /*#__PURE__*/_jsx("span", {
        className: styles.topBarBrandDivider,
        "aria-hidden": "true"
      }), /*#__PURE__*/_jsxs("span", {
        className: styles.topBarPath,
        children: [/*#__PURE__*/_jsx("button", {
          type: "button",
          className: styles.topBarCrumbBtn,
          onClick: goHome,
          children: "Evidence"
        }), crumbs.map(function (crumb, i) {
          return /*#__PURE__*/_jsxs(React.Fragment, {
            children: [/*#__PURE__*/_jsx("span", {
              className: styles.topBarSep,
              "aria-hidden": "true",
              children: "/"
            }), crumb.onClick ? /*#__PURE__*/_jsx("button", {
              type: "button",
              className: styles.topBarCrumbBtn,
              onClick: crumb.onClick,
              title: crumb.label,
              children: truncateMiddle(crumb.label, CRUMB_MAX_CHARS)
            }) : /*#__PURE__*/_jsx("span", {
              className: styles.topBarCurrent,
              title: crumb.label,
              "aria-current": "page",
              children: truncateMiddle(crumb.label, CRUMB_MAX_CHARS)
            })]
          }, "".concat(i, "-").concat(crumb.label));
        }), /*#__PURE__*/_jsx(CopyLinkButton, {
          className: styles.topBarCopyBtn
        })]
      })]
    }), /*#__PURE__*/_jsxs("span", {
      className: styles.topBarActions,
      children: [currentTestId && /*#__PURE__*/_jsx(LTTooltip, {
        text: "Switch test instance",
        direction: "bottom",
        customTriggerStyle: {
          display: 'inline-flex'
        },
        children: /*#__PURE__*/_jsx(TestListPopover, {
          currentId: currentTestId,
          triggerClassName: styles.topBarIconBtn
        })
      }), /*#__PURE__*/_jsx(LTButton, {
        variant: LTButton.Variant.DEFAULT,
        size: LTButton.Size.SMALL,
        label: "Search",
        ariaLabel: "Search tests, tabs, actions (".concat(PALETTE_SHORTCUT_LABEL, ")"),
        leadingIcon: /*#__PURE__*/_jsx(SearchGlyph, {}),
        trailingIcon: /*#__PURE__*/_jsx("span", {
          className: styles.topBarShortcut,
          children: PALETTE_SHORTCUT_LABEL
        }),
        onClick: onOpenSearch
      }), /*#__PURE__*/_jsx(LTButton, {
        variant: LTButton.Variant.DEFAULT,
        size: LTButton.Size.SMALL,
        label: ".evidence",
        ariaLabel: "Download the .evidence file",
        leadingIcon: /*#__PURE__*/_jsx(DownloadIcon, {
          size: 16,
          "aria-hidden": "true"
        }),
        onClick: function onClick() {
          return downloadPack();
        }
      }), hyperHref && /*#__PURE__*/_jsx(LTButton, {
        variant: LTButton.Variant.DEFAULT,
        size: LTButton.Size.SMALL,
        label: "View on HyperExecute",
        ariaLabel: "View on HyperExecute (opens in a new tab)",
        trailingIcon: /*#__PURE__*/_jsx(LinkExternalIcon, {
          size: 14,
          "aria-hidden": "true"
        }),
        onClick: function onClick() {
          return window.open(hyperHref, '_blank', 'noopener');
        },
        "data-testid": "evidence-hyperexecute-link"
      }), /*#__PURE__*/_jsx(HeaderLinks, {})]
    })]
  });
}
function OpenPack(_ref2) {
  var reportExtras = _ref2.reportExtras;
  var mode = useViewerMode();
  var header = usePackState(function (s) {
    return s.header;
  });
  var stats = usePackState(function (s) {
    return s.stats;
  });
  var detailOpen = usePackState(function (s) {
    return s.detail !== null;
  });
  var detailTitle = usePackState(function (s) {
    var _s$detail$header$titl, _s$detail;
    return (_s$detail$header$titl = (_s$detail = s.detail) === null || _s$detail === void 0 ? void 0 : _s$detail.header.title) !== null && _s$detail$header$titl !== void 0 ? _s$detail$header$titl : null;
  });
  var detailJobId = usePackState(function (s) {
    var _s$detail$header$hype, _s$detail2;
    return (_s$detail$header$hype = (_s$detail2 = s.detail) === null || _s$detail2 === void 0 ? void 0 : _s$detail2.header.hyperJobId) !== null && _s$detail$header$hype !== void 0 ? _s$detail$header$hype : null;
  });
  var detailTestId = usePackState(function (s) {
    var _s$detail$header$test, _s$detail3;
    return (_s$detail$header$test = (_s$detail3 = s.detail) === null || _s$detail3 === void 0 ? void 0 : _s$detail3.header.testId) !== null && _s$detail$header$test !== void 0 ? _s$detail$header$test : null;
  });
  // Run-level fallback: the first loaded row's job (a run is one job).
  var runJobId = usePackState(function (s) {
    var _iterator = _createForOfIteratorHelper(s.rows.values()),
      _step;
    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _row$externalId;
        var row = _step.value;
        var id = (_row$externalId = row.externalId) === null || _row$externalId === void 0 ? void 0 : _row$externalId.hyper_job_id;
        if (typeof id === 'string' && id !== '') return id;
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
    return null;
  });
  var issueCount = usePackState(function (s) {
    var _s$attention$totals$f, _s$attention;
    return (_s$attention$totals$f = (_s$attention = s.attention) === null || _s$attention === void 0 ? void 0 : _s$attention.totals.failures) !== null && _s$attention$totals$f !== void 0 ? _s$attention$totals$f : 0;
  });
  var scrollRef = useRef(null);
  // The scroll element ALSO lives in state: the virtualizer needs a
  // re-render once it exists (see InstanceList.scrollElement).
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    scrollEl = _useState2[0],
    setScrollEl = _useState2[1];
  var attachScroll = useCallback(function (el) {
    scrollRef.current = el;
    setScrollEl(el);
  }, []);
  // Mirrors the TMS run page: Report | Test Instances | Issues. Report is
  // the landing tab; the list renders only on its own tab. Hydration is
  // store-driven, so results keep loading while the user reads the report.
  var _useState3 = useState('report'),
    _useState4 = _slicedToArray(_useState3, 2),
    tab = _useState4[0],
    setTab = _useState4[1];
  // Search state lives here (not in InstanceList): the pane unmounts while
  // a detail view is open and the query must survive the round trip.
  var _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    instanceQuery = _useState6[0],
    setInstanceQuery = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    paletteOpen = _useState8[0],
    setPaletteOpen = _useState8[1];
  // Where the user was in the list when they opened a detail.
  var snapshotRef = useRef(null);
  var wasDetailOpen = useRef(false);
  var handleSelect = useCallback(function (id) {
    var _scrollRef$current$sc, _scrollRef$current;
    snapshotRef.current = {
      scrollTop: (_scrollRef$current$sc = (_scrollRef$current = scrollRef.current) === null || _scrollRef$current === void 0 ? void 0 : _scrollRef$current.scrollTop) !== null && _scrollRef$current$sc !== void 0 ? _scrollRef$current$sc : 0,
      rowId: id
    };
    openDetailWithUrl(id);
  }, []);

  // Tab changes flow through here so the ?tab= param stays in step (the
  // URL is the shareable record of the whole view).
  var goTab = useCallback(function (t) {
    setTab(t);
    writeTabParam(t);
  }, []);

  // Deep link replay + the store→URL mirror. applyDeepLink waits out
  // hydration (?test needs the row index, ?issue the attention index) and
  // opens the exact view; syncViewParams then keeps ?step/?dock/?issue
  // mirrored for the mount's lifetime. Browser Back/Forward re-derive the
  // view through the popstate listener.
  useEffect(function () {
    var cancelReplay = applyDeepLink(goTab);
    var stopSync = syncViewParams();
    var unlisten = listenForTestParam(goTab);
    return function () {
      cancelReplay();
      stopSync();
      unlisten();
    };
  }, [goTab]);

  // ⌘K / Ctrl+K opens the command palette anywhere on the open pack (run
  // view AND detail — the listener lives above both). The window event is
  // the host-facing entry point (the TMS header's Search button).
  useEffect(function () {
    var onKey = function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(function (v) {
          return !v;
        });
      }
    };
    var onOpenEvent = function onOpenEvent() {
      return setPaletteOpen(true);
    };
    var onDownloadEvent = function onDownloadEvent() {
      return downloadPack();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener(EV_OPEN_PALETTE_EVENT, onOpenEvent);
    window.addEventListener(EV_DOWNLOAD_PACK_EVENT, onDownloadEvent);
    return function () {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(EV_OPEN_PALETTE_EVENT, onOpenEvent);
      window.removeEventListener(EV_DOWNLOAD_PACK_EVENT, onDownloadEvent);
    };
  }, []);

  // Returning from a detail: restore the list scroll offset and put focus
  // back on the row that was opened.
  useEffect(function () {
    if (wasDetailOpen.current && !detailOpen) {
      var snap = snapshotRef.current;
      if (snap) {
        requestAnimationFrame(function () {
          var _document$getElementB;
          if (scrollRef.current) scrollRef.current.scrollTop = snap.scrollTop;
          (_document$getElementB = document.getElementById("ev-instance-row-".concat(snap.rowId))) === null || _document$getElementB === void 0 || _document$getElementB.focus();
        });
      }
    }
    wasDetailOpen.current = detailOpen;
  }, [detailOpen]);
  if (!header) return null;
  return /*#__PURE__*/_jsxs("div", {
    className: styles.page,
    children: [(stats === null || stats === void 0 ? void 0 : stats.degraded) && /*#__PURE__*/_jsx("div", {
      className: "ev-t-small ".concat(styles.degraded),
      role: "status",
      children: "This pack's host doesn't support Range requests \u2014 the whole file was downloaded once instead of streamed."
    }), /*#__PURE__*/_jsx(CommandPalette, {
      open: paletteOpen,
      onClose: function onClose() {
        return setPaletteOpen(false);
      },
      goTab: goTab
    }), mode !== 'edit' && /*#__PURE__*/_jsx(TopBar, {
      crumbs: [{
        label: TAB_LABELS[detailOpen ? 'instances' : tab],
        onClick: detailOpen ? function () {
          return closeDetailWithUrl();
        } : undefined
      }].concat(_toConsumableArray(detailOpen && detailTitle ? [{
        label: detailTitle
      }] : [])),
      jobId: detailJobId !== null && detailJobId !== void 0 ? detailJobId : runJobId,
      currentTestId: detailTestId,
      onOpenSearch: function onOpenSearch() {
        return setPaletteOpen(true);
      }
    }), /*#__PURE__*/_jsx(RunTabs, {
      active: detailOpen ? 'instances' : tab,
      onChange: function onChange(t) {
        // In place, not history.back(): the async popstate would
        // re-derive the OLD entry's ?tab and clobber this switch.
        if (detailOpen) closeDetailInPlace();
        goTab(t);
      },
      instanceCount: header.totals.tests,
      issueCount: issueCount
    }), detailOpen ?
    /*#__PURE__*/
    // Full-page detail below the tabs; the breadcrumb marks the drill-in.
    // Back / Escape / browser Back / breadcrumb all return to the list
    // with scroll + search intact.
    _jsxs(_Fragment, {
      children: [mode === 'edit' && /*#__PURE__*/_jsxs("nav", {
        className: styles.crumbBar,
        "aria-label": "You are here",
        children: [/*#__PURE__*/_jsx(LTBreadcrumbs
        // Whole instance name; only long ones middle-truncate (the
        // lib then shows the full name in its own tooltip).
        , {
          maxCharsPerItem: 60,
          items: [{
            key: 'instances',
            label: 'Test Instances',
            selected: false,
            onClick: function onClick() {
              return closeDetailWithUrl();
            }
          }, {
            key: 'current',
            label: detailTitle !== null && detailTitle !== void 0 ? detailTitle : '',
            selected: true
          }]
        }), /*#__PURE__*/_jsxs("span", {
          className: styles.crumbBarActions,
          children: [/*#__PURE__*/_jsx(CopyLinkButton, {
            className: styles.topBarCopyBtn
          }), detailTestId && /*#__PURE__*/_jsx(TestListPopover, {
            currentId: detailTestId,
            triggerClassName: styles.topBarIconBtn
          }), /*#__PURE__*/_jsx(HyperExecuteLink, {
            jobId: detailJobId
          })]
        })]
      }), /*#__PURE__*/_jsx(TestDetailMount, {})]
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [tab === 'report' && /*#__PURE__*/_jsxs("div", {
        className: styles.reportPane,
        children: [/*#__PURE__*/_jsx(RunHeader, {
          run: header
        }), /*#__PURE__*/_jsx(SummaryStrip, {
          run: header
        }), /*#__PURE__*/_jsx(NeedAttention, {
          onOpenIssue: function onOpenIssue(item) {
            goTab('issues');
            openIssueDrawer(item);
          }
        }), reportExtras]
      }), tab === 'instances' && /*#__PURE__*/_jsx("main", {
        ref: attachScroll,
        className: styles.scroll,
        children: /*#__PURE__*/_jsx(InstanceList, {
          scrollElement: scrollEl,
          query: instanceQuery,
          onQueryChange: setInstanceQuery,
          onSelect: handleSelect
        })
      }), tab === 'issues' && /*#__PURE__*/_jsx(IssuesTab, {}), tab === 'coverage' && /*#__PURE__*/_jsx(CoverageTab, {}), tab === 'explorer' && /*#__PURE__*/_jsx(Suspense, {
        fallback: null,
        children: /*#__PURE__*/_jsx(ExplorerTab, {})
      })]
    }), /*#__PURE__*/_jsx(RcaDrawerMount, {})]
  });
}
function RcaDrawerMount() {
  var drawerOpen = usePackState(function (s) {
    return s.issueDrawer !== null;
  });
  return drawerOpen ? /*#__PURE__*/_jsx(RcaDrawer, {}) : null;
}

/**
 * One PERSISTENT live region: screen readers only announce content that
 * changes after the region is mounted, so it must exist across every phase
 * and have its text swapped — a region mounted together with its text never
 * announces anything.
 */
function LiveStatus() {
  var phase = usePackState(function (s) {
    return s.phase;
  });
  var header = usePackState(function (s) {
    return s.header;
  });
  var progress = usePackState(function (s) {
    return s.progress;
  });
  var message = '';
  if (phase === 'loading') {
    message = 'Opening evidence pack';
  } else if (phase === 'gh-auth') {
    message = 'GitHub sign-in required to open this evidence pack.';
  } else if (phase === 'gh-choose') {
    message = 'This artifact holds several evidence packs — choose one.';
  } else if (phase === 'open' && header) {
    var hydrating = progress.done + progress.failed < progress.total;
    message = hydrating ? "Run loaded: ".concat(header.title, ", ").concat(header.totals.tests, " tests. Loading test details.") : "Run loaded: ".concat(header.title, ", ").concat(header.totals.tests, " tests.");
  }
  return /*#__PURE__*/_jsx("span", {
    className: "ev-sr-only",
    "aria-live": "polite",
    children: message
  });
}
export function AppShell(_ref3) {
  var reportExtras = _ref3.reportExtras;
  var phase = usePackState(function (s) {
    return s.phase;
  });
  var error = usePackState(function (s) {
    return s.error;
  });
  var ghAuth = usePackState(function (s) {
    return s.ghAuth;
  });
  var ghChoose = usePackState(function (s) {
    return s.ghChoose;
  });
  var mode = useViewerMode();
  var content;
  // The pre-open screens (gate/loading/error/landing) have no run toolbar, so
  // the standalone app header rides above them; the open pack brings its own.
  var withAppHeader = true;
  if (phase === 'landing') {
    content = /*#__PURE__*/_jsx(Landing, {
      onFile: function onFile(f) {
        return openPackFromInput(f);
      },
      onUrl: function onUrl(u) {
        return openPackFromInput(u);
      }
    });
  } else if (phase === 'gh-auth' && ghAuth) {
    content = /*#__PURE__*/_jsx(GhConnectGate, {
      ghAuth: ghAuth,
      onConnect: function onConnect() {
        return beginGhLogin(window.location.href);
      },
      onFile: function onFile(f) {
        return openPackFromInput(f);
      }
    });
  } else if (phase === 'gh-choose' && ghChoose) {
    content = /*#__PURE__*/_jsx(GhChooseScreen, {
      ghChoose: ghChoose,
      onChoose: function onChoose(e) {
        return chooseGhEntry(e);
      }
    });
  } else if (phase === 'loading') {
    content = /*#__PURE__*/_jsx(OpeningPack, {});
  } else if (phase === 'error' && error) {
    content = /*#__PURE__*/_jsx(ErrorScreen, {
      error: error,
      onReset: function onReset() {
        return void closePack();
      }
    });
  } else {
    content = /*#__PURE__*/_jsx(OpenPack, {
      reportExtras: reportExtras
    });
    withAppHeader = false;
  }

  // Host mounts (kaneai's /evidence route, mode 'edit') supply their own
  // chrome — the standalone header only belongs to the public page.
  var showAppHeader = withAppHeader && mode !== 'edit';
  return (
    /*#__PURE__*/
    // Scope class for every packaged style — prevents global bleed when the
    // component mounts inside a host app (kaneai's /evidence route).
    _jsxs("div", {
      className: "ltEvidenceViewer",
      children: [/*#__PURE__*/_jsx(LiveStatus, {}), showAppHeader ? /*#__PURE__*/_jsxs("div", {
        className: styles.chrome,
        children: [/*#__PURE__*/_jsx(AppHeader, {}), /*#__PURE__*/_jsx("div", {
          className: styles.chromeBody,
          children: content
        })]
      }) : content]
    })
  );
}